import { useAuthStore } from '@/store/auth.store';
import type { UserRole } from '@/types';

export function useAuth() {
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const appUser = useAuthStore((s) => s.appUser);
  const loading = useAuthStore((s) => s.loading);
  const initialized = useAuthStore((s) => s.initialized);

  const isAuthenticated = !!firebaseUser && !!appUser;
  const role = appUser?.role ?? null;

  function hasRole(...roles: UserRole[]): boolean {
    if (!role) return false;
    return roles.includes(role);
  }

  function isPlatformAdmin(): boolean {
    return role === 'PLATFORM_ADMIN';
  }

  function isClientAdmin(): boolean {
    return role === 'CLIENT_ADMIN';
  }

  function isCompanyAdmin(): boolean {
    return role === 'COMPANY_ADMIN';
  }

  function isOperator(): boolean {
    return role === 'OPERATOR';
  }

  function isAnalyst(): boolean {
    return role === 'ANALYST';
  }

  function canManageTenant(): boolean {
    return hasRole('PLATFORM_ADMIN');
  }

  function canManageCompany(): boolean {
    return hasRole('PLATFORM_ADMIN', 'CLIENT_ADMIN');
  }

  function canManageParkingLot(): boolean {
    return hasRole('PLATFORM_ADMIN', 'CLIENT_ADMIN', 'COMPANY_ADMIN');
  }

  function canManageUsers(): boolean {
    return hasRole('PLATFORM_ADMIN', 'CLIENT_ADMIN', 'COMPANY_ADMIN');
  }

  function canReviewInspections(): boolean {
    return hasRole('PLATFORM_ADMIN', 'CLIENT_ADMIN', 'COMPANY_ADMIN', 'ANALYST');
  }

  function canCreateInspections(): boolean {
    return hasRole('OPERATOR', 'COMPANY_ADMIN', 'CLIENT_ADMIN', 'PLATFORM_ADMIN');
  }

  return {
    firebaseUser,
    appUser,
    loading,
    initialized,
    isAuthenticated,
    role,
    hasRole,
    isPlatformAdmin,
    isClientAdmin,
    isCompanyAdmin,
    isOperator,
    isAnalyst,
    canManageTenant,
    canManageCompany,
    canManageParkingLot,
    canManageUsers,
    canReviewInspections,
    canCreateInspections,
    uid: firebaseUser?.uid ?? null,
    tenantId: appUser?.tenantId ?? null,
    companyId: appUser?.companyId ?? null,
    allowedCompanyIds: appUser?.allowedCompanyIds ?? [],
    parkingLotIds: appUser?.parkingLotIds ?? [],
  };
}
